import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSelector } from 'react-redux';
import NavigationLayoutDesktop from '../components/AppLayout/NavigationLayout/NavigationLayoutDesktop';
import NavigationLayoutMobile from '../components/AppLayout/NavigationLayout/NavigationLayoutMobile';
import ReaderDocumentContent from '../components/Reader/ReaderContent/ReaderDocumentContent';
import ReaderHead from '../components/Reader/ReaderHead';
import {
  fetchSiblings as fetchSiblingsAction
} from '../actions/mindItems.actions';
import {
  fetchAttachments as fetchAttachmentsAction,
  fetchProduct as fetchProductAction,
} from '../actions/product.actions';
import {
  selectMindItem,
  isMindItemInvalid as isMindItemInvalidSelector,
} from '../reducers/mindItems.selectors';
import { selectUser } from '../reducers/user.selectors';
import ReaderContextProvider, { ReaderContext } from '../components/Reader/ReaderContext';
import ReaderProductNavigation from '../components/Reader/ReaderProductNavigation';
import { routes } from '../constants';
import PdfReader from '../components/Reader/PdfReader/PdfReader';
import MasterLayout from '../components/AppLayout/MasterLayout';
import { useNavigationLayout }  from '../utils/useNavigationLayout';
import ReaderInfoSidebar from '../components/Reader/ReaderInfoSidebar';
import { ReaderScrollDirection } from '../types/reader';
import ReaderActionsMobile from '../components/Reader/ReaderControls/ReaderActionsMobile';
import { setCustomPageTitle } from '../utils/customTitle';
import useAction from '../utils/useAction';
import useRouter from '../utils/useRouter';
import ReaderVideoContent from '../components/Reader/ReaderContent/ReaderVideoContent/ReaderVideoContent';
import { isValidVideoItem } from '../utils/subdomain';
import DesignSwitchFeedbackDialog from '../components/modals/FeedbackDialogs/DesignSwitchFeedbackDialog';
import NewReader from '../components/NewReader/NewReader';
import { trackContentViewed } from '../utils/tracking';
import { useNewDesign } from '../state/DesignPreferenceContextProvider';
import { aiAssistantContext } from '../state/AIAssistantContextProvider';
import useScreenSize from '../utils/useScreenSize';
import AIAssistantToggler from '../components/NewReader/AIAssistant/AIAssistantToggler';

const ReaderPage = () => {
  return (
    <ReaderContextProvider>
      <ReaderPageContent />
    </ReaderContextProvider>
  );
};

const ReaderPageContent = () => {
  const [isPremiumPdfDocument, setIsPremiumPdfDocument] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  const { match, router } = useRouter();
  const { location: { query } } = match;
  const fetchProduct = useAction(fetchProductAction);
  const fetchSiblings = useAction(fetchSiblingsAction);

  const {
    setInitialPage,
    mindItemId: documentId,
    showNewReader,
  } = useContext(ReaderContext);

  const mindItem = useSelector(state => selectMindItem(state, documentId));

  const user = useSelector(selectUser);
  const isMindItemInvalid = useSelector(state => isMindItemInvalidSelector(state, documentId));

  const { renderNavigationMobileLayout, showSidebarRight, setShowSidebarRight } = useNavigationLayout();
  const [scrollDirection, setScrollDirection] = useState<ReaderScrollDirection>();
  const showPublicShare = user?.hasDocumentLevelAccess && mindItem?.fileType === 'video'
   && mindItem?.isShareable;
  const contentScrollTop = useRef<number>(0);
  const shouldHideAllMIActions = mindItem?.fileType === 'video' && !isValidVideoItem(mindItem?.publisher);
  const { prefersNewDesign, shouldShowDesignPreferenceSwitch } = useNewDesign();
  const { isAssistantAvailable } = useContext(aiAssistantContext);
  const { isLargeScreen } = useScreenSize();
  const showAIAssistantToggler = isAssistantAvailable && isLargeScreen && mindItem?.fileType === 'document';

  useEffect(() => {
    if (query && query.page) {
      const newInitPage = parseInt(query.page);
      setInitialPage(newInitPage > 0 ? newInitPage : 1);
    }
    else {
      setInitialPage(1);
    }
  }, [query]);

  useEffect(() => {
    if (mindItem) {
      fetchProduct(mindItem.product);
      fetchSiblings(mindItem.id, mindItem.product);
      fetchAttachmentsAction(mindItem.product);
      setCustomPageTitle(mindItem.title);

      if (user) {
        trackContentViewed(mindItem, prefersNewDesign, shouldShowDesignPreferenceSwitch);
      }
    }
  }, [mindItem?.product, mindItem?.product, mindItem?.id]);

  useEffect(() => {
    if (user?.hasDocumentLevelAccess && mindItem?.fileType === 'document')
      setIsPremiumPdfDocument(true);
    else
      setIsPremiumPdfDocument(false);
  }, [user?.hasDocumentLevelAccess, mindItem?.fileType]);

  if (isMindItemInvalid) {
    router.push(`/${routes.dashboard.root}`);
  }
  if (!user || !mindItem) {
    return <></>;
  }

  const handleInfoClick = () => {
    setShowSidebarRight(sideBarPosition => !sideBarPosition);
  };

  const handleSearchClick = () => {
    setIsSearchVisible(searchVisibility => !searchVisibility);
  };

  const handleScroll = (scrollTop: number) => {
    if (!renderNavigationMobileLayout) return;
    const didScrollDown = (scrollTop - contentScrollTop.current) > 10;
    const didScrollUp = (contentScrollTop.current - scrollTop) > 10;

    if (didScrollDown) {
      setScrollDirection(ReaderScrollDirection.DOWN);
    }

    if (didScrollUp) {
      setScrollDirection(ReaderScrollDirection.UP);
    }

    contentScrollTop.current = scrollTop;
  };

  const getReaderContent = () => {
    if (!mindItem || !user) return null;

    if (mindItem?.fileType === 'course') {
      return null;
    }

    if (mindItem?.fileType === 'video') {
      return <ReaderVideoContent
        mindItemId={mindItem.id}
        variant={renderNavigationMobileLayout ? 'mobile' : 'desktop'}
        showPublicShare={showPublicShare}
      />;
    }

    if (user.hasDocumentLevelAccess) {
      return <PdfReader
        isSearchVisible={isSearchVisible}
        setIsSearchVisible={setIsSearchVisible}
        onSearchClick={handleSearchClick}
        variant={renderNavigationMobileLayout ? 'mobile' : 'desktop'}
        onScroll={handleScroll}
      />;
    }
    else {
      return <ReaderDocumentContent
        pages={mindItem.pages}
        variant={renderNavigationMobileLayout ? 'mobile' : 'desktop'}
        onScroll={handleScroll}
      />;
    }
  };
  return <>
    <DesignSwitchFeedbackDialog />
    <MasterLayout
      additionalTopBarContent={showAIAssistantToggler && <AIAssistantToggler />}
    >
      {showNewReader ?
        <NewReader /> :
        <>
          {renderNavigationMobileLayout && <NavigationLayoutMobile
            head={
              <ReaderHead
                header={mindItem.title}
                showInfoIcon
                onInfoClick={handleInfoClick}
                isInfoIconActive={showSidebarRight}
                productId={mindItem.product}
              />
            }
            main={getReaderContent()}
            sidebarRight={
              <ReaderInfoSidebar
                mindItemId={documentId}
                variant='mobile'
                onClose={() => setShowSidebarRight(false)}
              />
            }
            actions={
              <ReaderActionsMobile
                hideDownloadAction={mindItem?.fileType === 'video'}
                showSearchIcon={mindItem?.fileType !== 'video' && user.hasDocumentLevelAccess}
                hideAllMIActions={shouldHideAllMIActions}
                onSearchClick={handleSearchClick}
              />
            }
            showSidebarRight={showSidebarRight}
            hideHeadAndActions={!isSearchVisible && scrollDirection === ReaderScrollDirection.DOWN}
          />}
          {!renderNavigationMobileLayout && <NavigationLayoutDesktop
            head={
              <ReaderHead
                header={mindItem.title}
                showInfoIcon
                onInfoClick={handleInfoClick}
                isInfoIconActive={showSidebarRight}
                showSearchIcon={isPremiumPdfDocument}
                onSearchClick={handleSearchClick}
                productId={mindItem.product}
              />
            }
            main={getReaderContent()}
            sidebarLeft={
              <ReaderProductNavigation
                mindItemId={mindItem.id}
                productId={mindItem.product}
                onNavigate={(pageIndex) => {
                  // Navigate to different page inside the current mindItem
                  setInitialPage(pageIndex);
                }}
              />}
            sidebarRight={
              <ReaderInfoSidebar
                mindItemId={documentId}
                variant='desktop'
                onClose={() => setShowSidebarRight(false)}
              />
            }
            showSidebarRight={showSidebarRight}
          />}
        </>
      }
    </MasterLayout>
  </>;
};

export default ReaderPage;
